import type {
  CompanyResponse,
  DeactivateProfilesResponse,
  DeduplicateListResponse,
  ImportListEmailsResponse,
  ListDuplicatesResponse,
  ListResponse,
  ProfileResponse,
  RestoreProfilesResponse,
  SetExpertValidationResponse,
} from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { pendingCountRequested } from '@/features/list/client-import-alerts';

import type { ListDetailGateway } from '../infrastructure/list-detail.gateway';
import {
  deactivateRequested,
  dedupeConfirmed,
  dedupePreviewRequested,
  deleteRequested,
  duplicateConfirmed,
  duplicateRequested,
  enrichmentRequested,
  expertValidationRequested,
  importEmailsRequested,
  loadRequested,
  restoreRequested,
} from './list-detail.actions';
import { selectDedupe, selectList } from './list-detail.selectors';
import { listDetailActions } from './list-detail.slice';

const DEFAULT_ERROR = 'Something went wrong. Please retry.';

/** Human message for the mutation failure banner (no domain error union needed). */
function toMessage(e: unknown): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : DEFAULT_ERROR;
}

/**
 * WORKER: load the list + all admin-visible resources in parallel. Re-run after
 * a mutation (`put(loadRequested(id))`); `loadStarted` keeps the current `list`
 * so the page never blanks during a background refresh.
 */
function* loadWorker(action: ReturnType<typeof loadRequested>) {
  const gateway: ListDetailGateway = yield getContext('listDetailGateway');
  const listId = action.payload;
  yield put(listDetailActions.loadStarted());
  try {
    const [list, profiles, companies, commentCount, removedProfiles]: [
      ListResponse,
      ProfileResponse[],
      CompanyResponse[],
      number,
      ProfileResponse[],
    ] = yield all([
      call(gateway.loadList, listId),
      call(gateway.loadProfiles, listId),
      call(gateway.loadCompanies, listId),
      call(gateway.loadCommentCount, listId),
      call(gateway.loadRemovedProfiles, listId),
    ]);
    yield put(
      listDetailActions.loadSucceeded({ list, profiles, companies, commentCount, removedProfiles }),
    );
  } catch {
    yield put(listDetailActions.loadFailed());
  }
}

/** WORKER: queue an enrichment session (no reload; the session runs server-side). */
function* enrichWorker(action: ReturnType<typeof enrichmentRequested>) {
  const gateway: ListDetailGateway = yield getContext('listDetailGateway');
  const list = (yield select(selectList)) as ListResponse | null;
  if (!list) return;
  yield put(listDetailActions.mutationStarted('enrich'));
  try {
    yield call(gateway.createEnrichmentSession, {
      listId: list.id,
      licenceId: list.licence_id,
      profileIds: action.payload.profileIds,
      force: action.payload.force,
    });
    yield put(listDetailActions.mutationSucceeded({ kind: 'enrich' }));
    // A client import leaves the Collection badge once its enrichment is launched (#748).
    yield put(pendingCountRequested());
  } catch (e) {
    yield put(
      listDetailActions.mutationFailed({ kind: 'enrich', message: toMessage(e) }),
    );
  }
}

/** WORKER: deactivate the selected profiles, then reload the list data. */
function* deactivateWorker(action: ReturnType<typeof deactivateRequested>) {
  const gateway: ListDetailGateway = yield getContext('listDetailGateway');
  const list = (yield select(selectList)) as ListResponse | null;
  if (!list) return;
  yield put(listDetailActions.mutationStarted('deactivate'));
  try {
    const res = (yield call(gateway.deactivateProfiles, {
      profileIds: action.payload.profileIds,
      scope: action.payload.scope,
      listId: list.id,
    })) as DeactivateProfilesResponse;
    yield put(
      listDetailActions.mutationSucceeded({
        kind: 'deactivate',
        result: { deactivatedCount: res.deactivated_count },
      }),
    );
    yield put(loadRequested(list.id));
  } catch (e) {
    yield put(
      listDetailActions.mutationFailed({
        kind: 'deactivate',
        message: toMessage(e),
      }),
    );
  }
}

/** WORKER: restore removed profiles back into the list, then reload. */
function* restoreWorker(action: ReturnType<typeof restoreRequested>) {
  const gateway: ListDetailGateway = yield getContext('listDetailGateway');
  const list = (yield select(selectList)) as ListResponse | null;
  if (!list) return;
  yield put(listDetailActions.mutationStarted('restore'));
  try {
    const res = (yield call(
      gateway.restoreProfiles,
      list.id,
      action.payload.profileIds,
    )) as RestoreProfilesResponse;
    yield put(
      listDetailActions.mutationSucceeded({
        kind: 'restore',
        result: { restoredCount: res.restored_count },
      }),
    );
    yield put(loadRequested(list.id));
  } catch (e) {
    yield put(
      listDetailActions.mutationFailed({ kind: 'restore', message: toMessage(e) }),
    );
  }
}

/** WORKER: validate or unvalidate profiles (#671), then reload so the column shows it. */
function* expertValidationWorker(action: ReturnType<typeof expertValidationRequested>) {
  const gateway: ListDetailGateway = yield getContext('listDetailGateway');
  const list = (yield select(selectList)) as ListResponse | null;
  if (!list) return;
  const { profileIds, validated } = action.payload;
  yield put(listDetailActions.mutationStarted('expert-validation'));
  try {
    const res = (yield call(
      gateway.setExpertValidation,
      profileIds,
      validated,
    )) as SetExpertValidationResponse;
    yield put(
      listDetailActions.mutationSucceeded({
        kind: 'expert-validation',
        result: { expertValidation: { validated, updatedCount: res.updated_count } },
      }),
    );
    yield put(loadRequested(list.id));
  } catch (e) {
    yield put(
      listDetailActions.mutationFailed({ kind: 'expert-validation', message: toMessage(e) }),
    );
  }
}

/** WORKER: permanently delete the list (page navigates away on `deleted`). */
function* deleteWorker() {
  const gateway: ListDetailGateway = yield getContext('listDetailGateway');
  const list = (yield select(selectList)) as ListResponse | null;
  if (!list) return;
  yield put(listDetailActions.mutationStarted('delete'));
  try {
    yield call(gateway.deleteList, list.id);
    yield put(listDetailActions.mutationSucceeded({ kind: 'delete' }));
    yield put(pendingCountRequested());
  } catch (e) {
    yield put(
      listDetailActions.mutationFailed({ kind: 'delete', message: toMessage(e) }),
    );
  }
}

/**
 * WORKER: import a Surf CSV's emails, then reload so the Email column shows
 * them. A file-level problem (empty, missing column) is a 400 and lands in
 * `mutationFailed` with the backend's message; row-level ones are in the result.
 */
function* importEmailsWorker(action: ReturnType<typeof importEmailsRequested>) {
  const gateway: ListDetailGateway = yield getContext('listDetailGateway');
  const list = (yield select(selectList)) as ListResponse | null;
  if (!list) return;
  yield put(listDetailActions.mutationStarted('import-emails'));
  try {
    const res = (yield call(
      gateway.importEmails,
      list.id,
      action.payload,
    )) as ImportListEmailsResponse;
    yield put(
      listDetailActions.mutationSucceeded({ kind: 'import-emails', result: { emailImport: res } }),
    );
    yield put(loadRequested(list.id));
  } catch (e) {
    yield put(
      listDetailActions.mutationFailed({ kind: 'import-emails', message: toMessage(e) }),
    );
  }
}

/** WORKER: count the list's duplicates for the chosen scope (no change). */
function* dedupePreviewWorker(action: ReturnType<typeof dedupePreviewRequested>) {
  const gateway: ListDetailGateway = yield getContext('listDetailGateway');
  const list = (yield select(selectList)) as ListResponse | null;
  if (!list) return;
  yield put(listDetailActions.dedupePreviewStarted(action.payload.scope));
  try {
    const preview = (yield call(
      gateway.findDuplicates,
      list.id,
      action.payload.scope,
    )) as ListDuplicatesResponse;
    yield put(listDetailActions.dedupePreviewSucceeded(preview));
  } catch (e) {
    yield put(listDetailActions.dedupePreviewFailed(toMessage(e)));
  }
}

/** WORKER: remove the duplicates of the previewed scope, then reload. */
function* dedupeWorker() {
  const gateway: ListDetailGateway = yield getContext('listDetailGateway');
  const list = (yield select(selectList)) as ListResponse | null;
  const { scope } = (yield select(selectDedupe)) as ReturnType<typeof selectDedupe>;
  if (!list || !scope) return;
  yield put(listDetailActions.mutationStarted('dedupe'));
  try {
    const res = (yield call(gateway.removeDuplicates, list.id, scope)) as DeduplicateListResponse;
    yield put(
      listDetailActions.mutationSucceeded({
        kind: 'dedupe',
        result: { dedupedCount: res.removed_count },
      }),
    );
    yield put(loadRequested(list.id));
  } catch (e) {
    yield put(listDetailActions.mutationFailed({ kind: 'dedupe', message: toMessage(e) }));
  }
}

/**
 * WORKER: open the Duplicate dialog once the licence's list names are in, so
 * the suggested name skips them. A failed read still opens it, with the list's
 * own name only: the suggestion may collide, the admin can still edit it.
 */
function* duplicateNamesWorker() {
  const gateway: ListDetailGateway = yield getContext('listDetailGateway');
  const list = (yield select(selectList)) as ListResponse | null;
  if (!list) return;
  yield put(listDetailActions.duplicateNamesStarted());
  try {
    const names = (yield call(gateway.loadLicenceListNames, list.licence_id)) as string[];
    yield put(listDetailActions.duplicateDialogOpened(names));
  } catch {
    yield put(listDetailActions.duplicateDialogOpened([list.name]));
  }
}

/** WORKER: create the copy (no reload: this list is unchanged). */
function* duplicateWorker(action: ReturnType<typeof duplicateConfirmed>) {
  const gateway: ListDetailGateway = yield getContext('listDetailGateway');
  const list = (yield select(selectList)) as ListResponse | null;
  if (!list) return;
  yield put(listDetailActions.mutationStarted('duplicate'));
  try {
    const copy = (yield call(
      gateway.duplicateList,
      list.id,
      action.payload.name.trim(),
    )) as ListResponse;
    yield put(
      listDetailActions.mutationSucceeded({
        kind: 'duplicate',
        result: { copy: { id: copy.id, name: copy.name } },
      }),
    );
    yield put(listDetailActions.duplicateCleared());
  } catch (e) {
    yield put(listDetailActions.mutationFailed({ kind: 'duplicate', message: toMessage(e) }));
  }
}

/** WATCHERS. */
export function* listDetailSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(enrichmentRequested.type, enrichWorker),
    takeLatest(deactivateRequested.type, deactivateWorker),
    takeLatest(restoreRequested.type, restoreWorker),
    takeLatest(expertValidationRequested.type, expertValidationWorker),
    takeLatest(deleteRequested.type, deleteWorker),
    takeLatest(importEmailsRequested.type, importEmailsWorker),
    takeLatest(dedupePreviewRequested.type, dedupePreviewWorker),
    takeLatest(dedupeConfirmed.type, dedupeWorker),
    takeLatest(duplicateRequested.type, duplicateNamesWorker),
    // `takeLeading`: a double click must not create two copies.
    takeLeading(duplicateConfirmed.type, duplicateWorker),
  ]);
}
